import type { createBrowserRouter } from "react-router";

import { routerLocationChange } from "Reducers/app/routerLocationChange";
import type { AppDispatch } from "Store/configureStore";

type DataRouter = ReturnType<typeof createBrowserRouter>;

/**
 * Bridges the data router's location state into Redux, replacing the
 * `useTrackPreviousRoutes` effect that ran from `Page.jsx`.
 *
 * Unlike the effect, this dispatches synchronously (before paint) and does not
 * depend on any view being mounted, so `state.app.routeHistory.params` tracks
 * the URL reliably. The action contract is unchanged: same `routerLocationChange`
 * action, same payload, same reducer consumers.
 *
 * Params come straight from `matches[].params`, which the router has already
 * decoded (`matchRoutes` runs `decodeURIComponent` per path segment). Matching
 * the old hook, we do not decode again — a second decode throws on values with a
 * literal `%` and corrupts any value holding a `%XX` sequence.
 *
 * @returns the unsubscribe function.
 */
export const trackRouterLocation = (
  router: DataRouter,
  store: { dispatch: AppDispatch }
) => {
  // Subscriptions never fire for the initial state, so dispatch once for the
  // synchronously-matched current state. This replaces the hook's mount dispatch.
  let lastKey = router.state.location.key;
  store.dispatch(
    routerLocationChange(
      router.state.location,
      router.state.matches.at(-1)?.params ?? {}
    )
  );

  return router.subscribe(state => {
    // `subscribe` fires on every state change (idle<->loading flips, fetcher
    // loads, revalidate()). Act only once the navigation has settled and the
    // location actually changed, swallowing redundant re-notifications that
    // would otherwise mint a fresh action id, reset the 401 modals, and churn
    // any downstream listeners. In a data router the location only flips to the
    // target at idle, so guarding on idle also keeps intermediate URLs out.
    if (state.navigation.state !== "idle") {
      return;
    }
    if (state.location.key === lastKey) {
      return;
    }
    lastKey = state.location.key;

    store.dispatch(
      routerLocationChange(state.location, state.matches.at(-1)?.params ?? {})
    );
  });
};
